import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUpRight, Clock, MonitorPlay, RotateCcw, SearchX, Square } from "lucide-react";
import { Link, useParams } from "react-router";
import { toast } from "sonner";
import { ApiError, api } from "../api";
import { EmptyState, PageSkeleton } from "../components/page";
import { PreviewStatusBadge } from "../components/preview-status";
import { TimelineItem } from "../components/previews/timeline-item";
import { TimeAgo } from "../components/time-ago";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../components/ui/breadcrumb";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { timeUntil } from "../lib/format";
import { actorName } from "../lib/people";
import { useIsManager } from "../lib/permissions";
import { PREVIEW_EXTEND_LABEL, PREVIEW_EXTEND_MINUTES } from "../lib/preview";
import { ACTIVE_PREVIEW } from "../types";

export function PreviewDetailPage() {
  const { id = "" } = useParams();
  const manager = useIsManager();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["preview", id],
    queryFn: () => api.preview(id),
    refetchInterval: 5_000,
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["preview", id] });
    void queryClient.invalidateQueries({ queryKey: ["previews"] });
  };
  const stop = useMutation({
    mutationFn: () => api.stopPreview(id),
    onSuccess: () => {
      toast.success("Preview stopped");
      refresh();
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Stop failed"),
  });
  const restart = useMutation({
    mutationFn: () => api.restartPreview(id),
    onSuccess: () => {
      toast.success("Preview relaunching");
      refresh();
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Retry failed"),
  });
  const extend = useMutation({
    mutationFn: () => api.extendPreview(id, PREVIEW_EXTEND_MINUTES),
    onSuccess: () => {
      toast.success("Preview extended by 3 days");
      refresh();
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Extend failed"),
  });

  if (query.isLoading) return <PageSkeleton />;
  const preview = query.data?.preview;
  if (!preview) {
    return (
      <EmptyState icon={SearchX} title="Preview not found" description="It may have been removed.">
        <Button variant="outline" asChild>
          <Link to={manager ? "/previews" : "/issues"}>
            {manager ? "Back to previews" : "Back to issues"}
          </Link>
        </Button>
      </EmptyState>
    );
  }
  const events = query.data?.events ?? [];
  const active = ACTIVE_PREVIEW.has(preview.status);
  const sha = preview.requestedSha ?? preview.headSha;

  return (
    <>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            {manager ? (
              <BreadcrumbLink asChild>
                <Link to="/previews">Previews</Link>
              </BreadcrumbLink>
            ) : preview.issueId ? (
              <BreadcrumbLink asChild>
                <Link to={`/issues/${preview.issueId}`}>Issue</Link>
              </BreadcrumbLink>
            ) : (
              <BreadcrumbPage>Preview</BreadcrumbPage>
            )}
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage className="font-mono">{preview.slug}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <PreviewStatusBadge preview={preview} />
            {preview.status === "ready" && preview.expiresAt && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="size-3" /> expires {timeUntil(preview.expiresAt)}
              </span>
            )}
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Preview <span className="font-mono">{preview.slug}</span>
          </h1>
          <p className="text-sm text-muted-foreground">
            <a href={preview.prUrl} target="_blank" rel="noreferrer" className="hover:underline">
              PR #{preview.prNumber}
            </a>
            {sha ? (
              <>
                {" "}
                at <span className="font-mono">{sha.slice(0, 7)}</span>
              </>
            ) : null}{" "}
            · generation {preview.generation} · created <TimeAgo at={preview.createdAt} />
            {actorName(preview.createdBy) ? ` by ${actorName(preview.createdBy)}` : ""}
          </p>
          {preview.statusMessage && <p className="text-sm text-muted-foreground">{preview.statusMessage}</p>}
          {preview.lastError && <p className="text-sm text-red-600 dark:text-red-400">{preview.lastError}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          {active ? (
            <>
              <Button variant="outline" disabled={extend.isPending} onClick={() => extend.mutate()}>
                <Clock /> {PREVIEW_EXTEND_LABEL}
              </Button>
              {manager && (
                <Button variant="destructive" disabled={stop.isPending} onClick={() => stop.mutate()}>
                  <Square /> Stop
                </Button>
              )}
            </>
          ) : preview.stopReason === "Issue marked done" ? (
            <p className="max-w-64 text-sm text-muted-foreground">
              This issue is done, so a preview cannot be created.
            </p>
          ) : (
            <Button disabled={restart.isPending} onClick={() => restart.mutate()}>
              <RotateCcw /> Retry
            </Button>
          )}
        </div>
      </div>

      {preview.status === "ready" && preview.apps && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Open the apps</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex flex-wrap gap-2">
              {(["admin", "client", "employer"] as const).map((app) =>
                preview.apps?.[app] ? (
                  <Button key={app} variant="outline" asChild>
                    <a href={preview.apps[app]} target="_blank" rel="noreferrer">
                      <MonitorPlay /> {app.charAt(0).toUpperCase() + app.slice(1)}{" "}
                      <ArrowUpRight className="size-3.5" />
                    </a>
                  </Button>
                ) : null,
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Sign in to Skynet when prompted. Demo credentials: admin <code>e2e@leaselab.test</code> /{" "}
              <code>asdfasdf</code>; the portals accept seeded emails with the deterministic OTP. The data is
              reseeded on every rebuild.
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Timeline</CardTitle>
        </CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <p className="text-sm text-muted-foreground">No events yet.</p>
          ) : (
            <ul className="space-y-2">
              {[...events].reverse().map((event) => (
                <TimelineItem key={event.seq} event={event} />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {preview.ecsTaskArn && (
        <p className="text-xs text-muted-foreground">
          Task: <span className="font-mono">{preview.ecsTaskArn}</span>
          {preview.privateIp ? (
            <>
              {" "}
              · IP <span className="font-mono">{preview.privateIp}</span>
            </>
          ) : null}
        </p>
      )}
    </>
  );
}
